import type { AnalysisTimeRangeKey } from '../../../../types/analysis'

const MS_PER_HOUR = 3_600_000
const MS_PER_DAY = 86_400_000

const assertNever = (value: never): never => {
  throw new Error(`Unsupported analysis time range: ${value}`)
}

export const timeRangeToDays = (timeRange: AnalysisTimeRangeKey): number => {
  switch (timeRange) {
    case '24H': return 1
    case '7D': return 7
    case '30D': return 30
    case '90D': return 90
    case '12M': return 365
    default: return assertNever(timeRange)
  }
}

export const timeRangeBucketCount = (timeRange: AnalysisTimeRangeKey): number => {
  switch (timeRange) {
    case '24H': return 24
    case '7D': return 7
    case '30D': return 30
    case '90D':
    case '12M': return 12
    default: return assertNever(timeRange)
  }
}

const startOfIsoWeek = (date: Date): Date => {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const isoDay = (start.getDay() + 6) % 7
  start.setDate(start.getDate() - isoDay)
  return start
}

const startOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1)

export const timeRangeToDateRange = (
  timeRange: AnalysisTimeRangeKey,
  now = new Date(),
): { from: Date; to: Date } => {
  const days = timeRangeToDays(timeRange)
  return {
    from: new Date(now.getTime() - days * MS_PER_DAY),
    to: now,
  }
}

export const bucketize = (
  timestamps: Array<Date | number | null | undefined>,
  timeRange: AnalysisTimeRangeKey,
  now = new Date(),
): number[] => {
  const validTimes = timestamps
    .map((value) => {
      if (value instanceof Date) return value.getTime()
      if (typeof value === 'number' && Number.isFinite(value)) return value
      return null
    })
    .filter((time): time is number => time !== null)

  if (timeRange === '24H') {
    const bucketCount = 24
    const bucketSize = MS_PER_HOUR
    const startTime = now.getTime() - bucketCount * bucketSize
    const buckets = new Array(bucketCount).fill(0)

    for (const time of validTimes) {
      if (time >= startTime && time <= now.getTime()) {
        const index = Math.min(bucketCount - 1, Math.max(0, Math.floor((time - startTime) / bucketSize)))
        buckets[index] += 1
      }
    }
    return buckets
  }

  if (timeRange === '90D' || timeRange === '12M') {
    const bucketStarts = Array.from({ length: 12 }, (_, index) => {
      if (timeRange === '90D') {
        const week = startOfIsoWeek(now)
        week.setDate(week.getDate() - (11 - index) * 7)
        return week
      }
      const month = startOfMonth(now)
      month.setMonth(month.getMonth() - (11 - index))
      return month
    })
    const startTime = bucketStarts[0].getTime()
    const buckets = new Array(12).fill(0)

    for (const time of validTimes) {
      if (time < startTime || time > now.getTime()) continue
      for (let index = bucketStarts.length - 1; index >= 0; index -= 1) {
        if (time >= bucketStarts[index].getTime()) {
          buckets[index] += 1
          break
        }
      }
    }
    return buckets
  }

  const bucketCount = timeRangeBucketCount(timeRange)
  const bucketSize = MS_PER_DAY
  const startTime = now.getTime() - bucketCount * bucketSize
  const buckets = new Array(bucketCount).fill(0)

  for (const time of validTimes) {
    if (time >= startTime && time <= now.getTime()) {
      const index = Math.min(bucketCount - 1, Math.max(0, Math.floor((time - startTime) / bucketSize)))
      buckets[index] += 1
    }
  }
  return buckets
}

export const calculateDelta = (points: number[]): number => {
  if (points.length < 2) return 0
  return (points.at(-1) ?? 0) - (points.at(-2) ?? 0)
}
